<template>
  <div
    v-if="modelValue"
    :id="$attrs.id as string"
    class="popup p-4"
    :class="[$attrs.class, { 'popup--fullscreen': fullScreen }]"
  >
    <div
      class="popup__backdrop"
      :class="{ 'popup__backdrop--clickable': closeable }"
      aria-hidden="true"
      @click="closeable && handleClose()"
    />
    <slot name="outside" />
    <div class="popup__inner m-auto flex size-full flex-col justify-center">
      <div
        ref="content"
        class="popup__content relative mx-auto w-full overflow-y-auto transition-maxWidth p-4 py-8 md:py-8 lg:py-8"
        :style="{ maxWidth }"
        :class="{ 'desktop:overflow-y-hidden': props.disableScroll }"
      >
        <div
          v-if="fullScreen"
          class="tablet:hidden flex flex-row items-center justify-between w-full bg-gold-primary-10 tablet:bg-transparent px-5 py-[14px] mb-6"
        >
          <div :class="showBackButton ? '' : 'opacity-0'">
            <UIButton
              id="back"
              variant="ghost"
              left-icon="arrow-big-filled-left"
              class="fill-gold-primary-20 !text-black-100"
              @click="$router.back()"
            >
              {{ t('common.actions.back') }}
            </UIButton>
          </div>
          <UILogo
            :link="false"
            :size="22"
            class="tablet:hidden"
          />
        </div>
        <div
          v-if="!hideTitle"
          class="popup__content-header mt-4"
        >
          <div class="popup__content-header-title">
            <slot name="header" />
          </div>
          <UIIcon
            v-if="closeable"
            :class="{ 'popup__content-header-close--hidden': !closeable }"
            class="popup__content-header-close"
            name="big-close"
            @click="handleClose"
          />
        </div>
        <div
          class="popup__content-body"
          :class="bodyClass"
        >
          <slot name="body" />
        </div>
        <div
          v-if="!hideButtons"
          class="popup__content-buttons"
          :class="{ 'popup__content-buttons-centered': centeredButtons }"
        >
          <slot name="buttons">
            <UIButton
              v-if="!hideCloseButton"
              id="popup-close"
              compact
              variant="secondary"
              @click="handleClose"
            >
              {{ closeText }}
            </UIButton>
            <UIButton
              id="popup-apply"
              compact
              :loading="loading"
              class="popup__content-buttons-apply"
              @click="emit('apply')"
            >
              {{ buttonText }}
            </UIButton>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    closeable?: boolean;
    closeText?: string;
    buttonText?: string;
    loading?: boolean;
    disableBodyLock?: boolean;
    hideCloseButton?: boolean;
    hideTitle?: boolean;
    hideButtons?: boolean;
    fullScreen?: boolean;
    visible?: boolean;
    centeredButtons?: boolean;
    maxWidth?: CSSStyleDeclaration['maxWidth'];
    disableScroll?: boolean;
    bodyClass?: string;
    showBackButton?: boolean;
  }>(),
  {
    closeable: false,
    // TODO: rename it
    disableBodyLock: false,
    hideCloseButton: false,
    centeredButtons: false,
    maxWidth: '440px',
    disableScroll: false,
    showBackButton: false,
  },
);

const { t } = useI18n();

// Shadow the optional props so the template's bare `closeText`/`buttonText` references
// stay reactive to locale switches (a plain string default in `withDefaults` wouldn't be).
const closeText = computed(() => props.closeText ?? t('common.actions.close'));
const buttonText = computed(() => props.buttonText ?? t('common.actions.apply'));

const modelValue = defineModel<boolean>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'apply'): void;
}>();

const content = ref<HTMLDivElement | null>(null);

const { isMobile } = useAppBreakpoints();

onClickOutside(content, () => {
  if (isMobile) return;

  if (props.closeable) {
    handleClose();
  }
});

onKeyStroke('Escape', () => {
  if (props.closeable) {
    handleClose();
  }
});

const { lock, unlock } = useBodyScrollLock();

const handleClose = () => {
  modelValue.value = false;
  emit('close');
  unlock();
};

watch(
  () => modelValue.value,
  (newValue) => {
    if (!newValue) {
      unlock();
    } else if (!props.disableBodyLock) {
      lock();
    }
  },
);

onMounted(() => {
  if (!props.disableBodyLock && modelValue.value) {
    lock();
  }
});

onBeforeUnmount(() => {
  unlock();
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins.scss' as *;
@use '@/styles/variables.scss' as *;

.popup {
  position: fixed;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
  z-index: calc(var(--z-index-sticky-footer, 9999) + 1);

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(3px);

    &--clickable {
      cursor: pointer;
    }
  }

  &__inner {
    position: relative;
    z-index: 1;
    pointer-events: none;
  }

  &__content {
    pointer-events: auto;
  }

  &--fullscreen {
    position: fixed;
    z-index: calc(var(--z-index-sticky-footer, 9999) + 1);
    padding: 0;

    .popup__content {
      @media screen and (max-width: 1023px) {
        width: 100vw;
        max-height: 100dvh;
        max-width: unset;
        border-radius: 12px;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 16px;
    box-shadow: 0px 2px 10px 0px rgba(15, 21, 29, 0.12);

    @media screen and (max-width: 1023px) {
      border-radius: 12px;
      padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px));
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-align: center;
      margin-bottom: 16px;

      @media screen and (max-width: 1023px) {
        margin-inline-start: 0;
        margin-bottom: 8px;
      }

      &-title {
        width: 100%;
        @include headline3();
      }

      &-close {
        position: absolute;
        top: 16px;
        inset-inline-end: 16px;
        display: block;
        cursor: pointer;

        &--hidden {
          visibility: hidden;
          opacity: 0;
        }
      }
    }

    &-body {
      @include body2();
      text-align: center;
      margin-bottom: 24px;

      @media screen and (max-width: 1023px) {
        padding: 16px;
      }
    }

    &-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 16px;

      @media screen and (max-width: 1023px) {
        margin-top: auto;
        .button {
          flex-grow: 1;
        }
      }

      &-centered {
        justify-content: center;
      }
    }
  }
}
</style>
